/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import dayjs, { Dayjs } from "dayjs";
import { Case, Default, Switch } from "react-if";
import { useLocation } from "react-router-dom";
import { Spacer, Text, theme } from "@nordcloud/gnui";
import { dateFormat } from "~/constants";
import { ChartType, Period } from "../types";

type Props = {
  period: Period;
  startDate: string;
  endDate: string;
  chartType: ChartType;
};

export function ChartPeriodData({
  period,
  startDate,
  endDate,
  chartType,
}: Props) {
  const { pathname } = useLocation();

  const isBusinessContextTopApplications =
    pathname.includes("/business-contexts") &&
    chartType === ChartType.COST_PER_APPLICATION;
  const chartTitle = isBusinessContextTopApplications
    ? "Top Spending Applications from "
    : "";
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  return (
    <>
      <Spacer height={theme.spacing.spacing04} />
      <Text weight="medium" mb="0">
        {chartTitle}
        <Switch>
          <Case condition={period === Period.MONTH}>
            {generateTitleDate(start, end, isBusinessContextTopApplications)}
          </Case>
          <Case condition={period === Period.QUARTER}>
            {generateFullTitleDate(
              start,
              end,
              isBusinessContextTopApplications
            )}
          </Case>
          <Case condition={period === Period.YEAR}>
            {generateMonthlyTitleDate(
              start,
              end,
              isBusinessContextTopApplications
            )}
          </Case>
          <Case condition={period === Period.RANGE}>
            {generateFullTitleDate(
              start,
              end,
              isBusinessContextTopApplications
            )}
          </Case>
          <Default>
            {generateFullTitleDate(
              start,
              end,
              isBusinessContextTopApplications
            )}
          </Default>
        </Switch>
      </Text>
    </>
  );
}

function getPunctuationMark(isBusinessConextTopApplications: boolean): string {
  return isBusinessConextTopApplications ? "to" : "-";
}

function generateTitleDate(
  start: Dayjs,
  end: Dayjs,
  isBusinessConextTopApplications: boolean
) {
  return `${start.format(dateFormat.dayShort)} ${getPunctuationMark(
    isBusinessConextTopApplications
  )}  ${end.format(dateFormat.dayMonthYear)}`;
}

function generateFullTitleDate(
  start: Dayjs,
  end: Dayjs,
  isBusinessConextTopApplications: boolean
) {
  return `${start.format(dateFormat.dayMonthYear)} ${getPunctuationMark(
    isBusinessConextTopApplications
  )}  ${end.format(dateFormat.dayMonthYear)}`;
}

function generateMonthlyTitleDate(
  start: Dayjs,
  end: Dayjs,
  isBusinessConextTopApplications: boolean
) {
  return `${start.format(
    dateFormat.monthYearShortNoComma
  )} ${getPunctuationMark(isBusinessConextTopApplications)}  ${end.format(
    dateFormat.monthYearShortNoComma
  )}`;
}
